import { memo, useCallback, useState } from 'react';

interface NumberFieldBaseProps {
  className?: string;
  id?: string;
  step?: string;
  min?: number;
  max?: number;
  placeholder?: string;
  required?: boolean;
  /** Parse with parseInt instead of parseFloat (whole minutes, kW, …). */
  integer?: boolean;
}

/**
 * `allowEmpty` fields model "not set" as `undefined`; the rest always hold a
 * number, and emptying them just restores the committed value on blur.
 */
export type NumberFieldProps = NumberFieldBaseProps &
  (
    | { allowEmpty: true; value: number | undefined; onChange: (value: number | undefined) => void }
    | { allowEmpty?: false; value: number; onChange: (value: number) => void }
  );

/**
 * Controlled number input that can be emptied while it is being typed in.
 *
 * A plain `value={someNumber}` input cannot: the change handler has to turn
 * the empty string into *some* number, React re-renders that number straight
 * back into the box, and a field showing `0` becomes impossible to clear (you
 * can only type digits after the 0). So the raw text is kept in local state
 * while the field is being edited — an empty box survives the re-render — and
 * only parseable, in-range values are committed upwards. The draft is dropped
 * on blur, so the field then re-renders from form state.
 */
export const NumberField = memo(function NumberField(props: NumberFieldProps) {
  const {
    value,
    onChange,
    allowEmpty = false,
    integer = false,
    min,
    max,
    ...inputProps
  } = props as NumberFieldBaseProps & {
    value: number | undefined;
    onChange: (value: number | undefined) => void;
    allowEmpty?: boolean;
  };

  const [draft, setDraft] = useState<string | null>(null);

  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const raw = e.target.value;
      setDraft(raw);

      if (raw.trim() === '') {
        if (allowEmpty) onChange(undefined);
        return;
      }

      const parsed = integer ? parseInt(raw, 10) : parseFloat(raw);
      if (Number.isNaN(parsed)) return;
      // Out-of-range input stays in the draft (so it can be corrected) but is
      // never committed.
      if (min !== undefined && parsed < min) return;
      if (max !== undefined && parsed > max) return;

      onChange(parsed);
    },
    [allowEmpty, integer, min, max, onChange]
  );

  const handleBlur = useCallback(() => setDraft(null), []);

  return (
    <input
      type="number"
      {...inputProps}
      min={min}
      max={max}
      value={draft ?? (value === undefined ? '' : String(value))}
      onChange={handleChange}
      onBlur={handleBlur}
    />
  );
});

export default NumberField;
